/**
 * Cost Dashboard API - Visibility into AI spending.
 */

import { apiClient } from './client';

// ================================================================
// Types
// ================================================================

export interface CostSummary {
  tenant_id: string | null;
  current_month_cost_usd: number;
  max_monthly_budget_usd: number | null;
  remaining_budget_usd: number | null;
  percent_used: number;
  budget_exhausted: boolean;
  pause_on_limit: boolean;
  last_updated: string;
}

export interface DailyCost {
  date: string;
  cost_usd: number;
  operations_count: number;
}

export interface CostHistory {
  tenant_id: string | null;
  period: string;
  total_cost_usd: number;
  daily_costs: DailyCost[];
  avg_daily_cost_usd: number;
}

export interface OperationCost {
  operation: string;
  cost_usd: number;
  count: number;
  percent_of_total: number;
}

export interface CostBreakdownByOperation {
  tenant_id: string | null;
  period: string;
  total_cost_usd: number;
  by_operation: OperationCost[];
}

export interface CostProjection {
  tenant_id: string | null;
  current_month_cost_usd: number;
  projected_month_end_usd: number;
  days_remaining: number;
  avg_daily_rate_usd: number;
  will_exceed_budget: boolean;
  projected_overage_usd: number | null;
}

// ================================================================
// API Methods
// ================================================================

export const costsApi = {
  /**
   * Get current month cost summary.
   */
  getSummary: () => apiClient.get<CostSummary>('/cost/usage'),

  /**
   * Get cost history over time.
   * @param period - "7d", "30d", or "90d"
   */
  getHistory: (period: '7d' | '30d' | '90d' = '30d') =>
    apiClient.get<CostHistory>('/cost/history', { params: { period } }),

  /**
   * Get cost breakdown by operation type.
   * @param period - "7d", "30d", or "90d"
   */
  getBreakdown: (period: '7d' | '30d' | '90d' = '30d') =>
    apiClient.get<CostBreakdownByOperation>('/cost/breakdown', { params: { period } }),

  /**
   * Get cost projection for month end.
   */
  getProjection: () => apiClient.get<CostProjection>('/cost/projection'),
};
